import { useEffect, useState } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { Link } from "@/components/LocalLink";
import { productBySlug, formatCad, daysForLb, DOGS } from "@/data/catalog";
import { PageShell, usePageMeta, useJsonLd, productLd } from "@/components/PageShell";
import { BuyPanel } from "@/components/ProductBuyPanel";
import { BuyButton } from "@/components/BuyButton";
import { DrawerReviews } from "@/components/Reviews";
import { dogName } from "@/components/HomeSections";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";

const Gallery = ({ p }) => {
  const [i, setI] = useState(0);
  const many = p.gallery.length > 1;
  return (
    <div className="gallery" data-testid="product-gallery" style={many ? undefined : { gridTemplateColumns: "1fr" }}>
      {many && (
        <div className="thumbs">
          {p.gallery.map((src, n) => (
            <div key={src} className={i === n ? "on" : ""}>
              <button type="button" onClick={() => setI(n)} aria-label={`Show image ${n + 1}`} data-testid={`gallery-thumb-${n}`} style={{ width: "100%", height: "100%" }}><img src={src} alt="" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="main-img"><img src={p.gallery[i]} alt={i ? `${p.name} jar outdoors` : `Amber glass jar of ${p.name}`} data-testid="gallery-main" /></div>
      {many && <div className="dots">{p.gallery.map((src, n) => <i key={src} style={{ background: n === i ? "var(--brass)" : undefined }} />)}</div>}
    </div>
  );
};

export const DogNote = ({ note }) => {
  const dog = DOGS.find((d) => d.name === note.dog);
  return (
    <div className="note" data-testid="dog-note">
      <div className="eyebrow" style={{ marginBottom: 12 }}>Notes from the Pack</div>
      <q>{note.text}</q>
      <small>{dog ? `${dogName(dog)}, ${dog.title}` : note.dog} · a note from the Founding Four, not a customer review</small>
    </div>
  );
};

const StickyBar = ({ p }) => {
  const { weightLb } = useCart();
  const { t } = useLang();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  const lasts = weightLb > 0 && p.id !== "discovery_dram" ? t(` · lasts your dog ${daysForLb(weightLb)} days`, ` · dure ${daysForLb(weightLb)} jours`) : "";
  return (
    <div className="sticky sticky-m" data-testid="sticky-buy-bar">
      <div className="t" data-no-translate><b>{p.name}</b><span>{formatCad(p.priceCents).replace(" CAD", "")}{lasts}</span></div>
      <BuyButton productIds={p.id} className="btn btn-primary" testid="sticky-buy" />
    </div>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const p = productBySlug(slug);
  usePageMeta(p ? `${p.name} · Dirty Paws Apothecary` : "Dirty Paws Apothecary", p ? `${p.lead} ${p.leadRest}` : "");
  useJsonLd("product-schema", p ? productLd(p) : null);
  if (!p) return <Navigate to="/shop" replace />;
  return (
    <PageShell testid={`product-page-${p.id}`} page="product">
      <div className="wrap pdpwrap">
        <div className="crumbs"><Link to="/shop">Shop</Link> &nbsp;/&nbsp; {p.name}</div>
        <div className="pdp"><Gallery p={p} /><BuyPanel p={p} /></div>
      </div>
      <div className="wrap"><div className="below" data-testid="product-below">
        <div><div className="eyebrow">The story of the jar</div><p data-testid="product-story" style={{ marginTop: 10 }}>{p.story}</p></div>
        <div>
          {p.dogNote && <DogNote note={p.dogNote} />}
          <DrawerReviews product={p} reviewEmail={params.has("email") ? params.get("email") : null} />
        </div>
      </div></div>
      <StickyBar p={p} />
    </PageShell>
  );
}
